import SEOHead from "@/components/SEOHead";

const CGU = () => {
  return (
    <div className="min-h-screen bg-background py-16">
      <SEOHead
        title="CGU | Immovox - Conditions d'utilisation"
        description="Consultez les conditions générales d'utilisation du service Immovox, assistant vocal IA pour agences immobilières."
        canonical="/cgu"
      />
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold text-foreground mb-8">Conditions Générales d'Utilisation</h1>
        
        <p className="text-muted-foreground mb-8">
          Dernière mise à jour : 2 janvier 2025
        </p>

        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 1 - Objet</h2>
            <p className="text-muted-foreground">
              Les présentes Conditions Générales d'Utilisation (CGU) définissent les modalités 
              d'utilisation du service Immovox, une solution de qualification téléphonique automatisée 
              par intelligence artificielle destinée aux agences immobilières.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 2 - Définitions</h2>
            <ul className="text-muted-foreground list-disc pl-6 space-y-2">
              <li><strong>Service :</strong> la plateforme Immovox de qualification téléphonique IA accessible depuis immovox.fr</li>
              <li><strong>Client :</strong> l'agence immobilière ou le professionnel souscrivant au Service</li>
              <li><strong>Utilisateur :</strong> tout collaborateur du Client ayant accès au Service</li>
              <li><strong>Appelant :</strong> toute personne contactant le numéro de téléphone virtuel du Client</li>
              <li><strong>Ticket :</strong> fiche synthétique générée suite à un appel qualifié</li>
              <li><strong>Bot vocal :</strong> assistant IA traitant les appels entrants</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 3 - Inscription et compte</h2>
            <p className="text-muted-foreground mb-4">
              L'accès au Service nécessite la création d'un compte professionnel. Le Client garantit 
              l'exactitude des informations fournies lors de l'inscription (raison sociale, SIRET, 
              coordonnées professionnelles).
            </p>
            <p className="text-muted-foreground">
              Le Client est responsable de la gestion des accès de ses Utilisateurs et de la 
              confidentialité des identifiants. Toute activité réalisée depuis un compte est présumée 
              effectuée par son titulaire.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 4 - Description du Service</h2>
            <p className="text-muted-foreground mb-4">
              Immovox propose une solution de standard téléphonique intelligent comprenant :
            </p>
            <ul className="text-muted-foreground list-disc pl-6 space-y-2">
              <li>Un numéro de téléphone virtuel dédié (français)</li>
              <li>Un bot vocal IA répondant aux appels entrants 24h/24, 7j/7</li>
              <li>La qualification automatique des intentions (achat, vente, location, estimation...)</li>
              <li>La génération de tickets synthétisant chaque appel</li>
              <li>Un tableau de bord de suivi des appels et statistiques</li>
              <li>La configuration personnalisable des comportements du bot</li>
              <li>Le transfert d'appel conditionnel vers un collaborateur</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 5 - Tarifs et paiement</h2>
            <p className="text-muted-foreground mb-4">
              Les tarifs en vigueur sont affichés sur la page Tarifs du site. L'abonnement inclut :
            </p>
            <ul className="text-muted-foreground list-disc pl-6 space-y-2">
              <li>L'accès illimité à la plateforme pour tous les Utilisateurs du Client</li>
              <li>Un numéro de téléphone français inclus</li>
              <li>Les appels entrants traités par le bot (dans la limite du fair use)</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              Le paiement s'effectue par carte bancaire via Stripe. L'abonnement est mensuel et 
              renouvelé automatiquement sauf résiliation. Le Client peut résilier à tout moment 
              depuis son espace personnel ; la résiliation prend effet à la fin de la période payée.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 6 - Obligations du Client</h2>
            <p className="text-muted-foreground mb-4">
              Le Client s'engage à :
            </p>
            <ul className="text-muted-foreground list-disc pl-6 space-y-2">
              <li>Utiliser le Service conformément à sa destination professionnelle</li>
              <li>Informer ses appelants que les conversations sont enregistrées et analysées par IA</li>
              <li>Configurer le bot de manière conforme à la réglementation applicable</li>
              <li>Ne pas utiliser le Service à des fins frauduleuses, illégales ou de spam</li>
              <li>Ne pas tenter de contourner les mesures de sécurité ou de reverse-engineering</li>
              <li>Respecter la propriété intellectuelle d'Immovox</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 7 - Obligations d'Immovox</h2>
            <p className="text-muted-foreground mb-4">
              Immovox s'engage à :
            </p>
            <ul className="text-muted-foreground list-disc pl-6 space-y-2">
              <li>Assurer la disponibilité du Service (objectif de 99,5% hors maintenance planifiée)</li>
              <li>Sécuriser les données conformément aux standards de l'industrie</li>
              <li>Fournir un support technique réactif (email sous 24h ouvrées)</li>
              <li>Notifier les maintenances programmées au moins 48h à l'avance</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 8 - Données personnelles</h2>
            <p className="text-muted-foreground">
              Le traitement des données personnelles est décrit dans notre{" "}
              <a href="/confidentialite" className="text-accent hover:underline">Politique de Confidentialité</a>.
              <br /><br />
              <strong>Important :</strong> Le Client est responsable de traitement des données de ses 
              Appelants. Immovox agit en qualité de sous-traitant au sens du RGPD pour le traitement 
              des appels. Un accord de sous-traitance (DPA) est disponible sur demande.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 9 - Propriété intellectuelle</h2>
            <p className="text-muted-foreground">
              Le Service, ses fonctionnalités, algorithmes, interfaces et documentation sont la 
              propriété exclusive de KALYVOX SASU, exploités sous la marque Immovox. Le Client dispose 
              d'un droit d'utilisation personnel, non exclusif et non transférable pendant la durée de 
              son abonnement.
              <br /><br />
              Les données saisies par le Client restent sa propriété. Immovox dispose d'un droit 
              d'utilisation limité à la fourniture du Service et, de manière anonymisée, à 
              l'amélioration des algorithmes d'IA.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 10 - Limitation de responsabilité</h2>
            <p className="text-muted-foreground mb-4">
              Le Service repose sur des technologies d'intelligence artificielle qui peuvent comporter 
              des erreurs d'interprétation ou des limitations. Immovox ne garantit pas l'exactitude 
              parfaite des qualifications et transcriptions.
            </p>
            <p className="text-muted-foreground">
              La responsabilité d'Immovox est limitée au montant des sommes versées par le Client 
              au cours des 12 derniers mois. Immovox ne saurait être tenu responsable des dommages 
              indirects, perte de chiffre d'affaires ou préjudice commercial.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 11 - Résiliation</h2>
            <p className="text-muted-foreground mb-4">
              <strong>Par le Client :</strong> résiliation possible à tout moment depuis l'espace personnel. 
              L'accès au Service est maintenu jusqu'à la fin de la période de facturation en cours.
            </p>
            <p className="text-muted-foreground mb-4">
              <strong>Par Immovox :</strong> en cas de manquement grave aux CGU (non-paiement, 
              utilisation frauduleuse), Immovox peut suspendre ou résilier le compte après mise en 
              demeure restée sans effet pendant 15 jours.
            </p>
            <p className="text-muted-foreground">
              <strong>Portabilité :</strong> à la résiliation, le Client peut demander l'export de ses 
              données dans un format structuré (JSON/CSV) pendant 30 jours.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 12 - Force majeure</h2>
            <p className="text-muted-foreground">
              Aucune des parties ne sera responsable d'un retard ou défaut d'exécution résultant 
              d'événements de force majeure au sens de l'article 1218 du Code civil (catastrophe 
              naturelle, pandémie, panne généralisée de télécommunications, etc.).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 13 - Modification des CGU</h2>
            <p className="text-muted-foreground">
              Immovox peut modifier les présentes CGU à tout moment. Les modifications substantielles 
              seront notifiées par email au moins 30 jours avant leur entrée en vigueur. L'utilisation 
              continue du Service après cette date vaut acceptation des nouvelles CGU.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Article 14 - Droit applicable et litiges</h2>
            <p className="text-muted-foreground">
              Les présentes CGU sont soumises au droit français. En cas de litige, les parties 
              s'efforceront de trouver une solution amiable. À défaut, les tribunaux de Toulouse 
              seront seuls compétents.
              <br /><br />
              Conformément à l'article L.612-1 du Code de la consommation (pour les clients 
              non-professionnels), le Client peut recourir gratuitement à un médiateur de la 
              consommation.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-border">
          <a href="/" className="text-accent hover:underline">← Retour à l'accueil</a>
        </div>
      </div>
    </div>
  );
};

export default CGU;